import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {
  SelectedTeamService,
  SelectedWorkspaceService,
} from '@tokenbox/services';
import { delay, interval, map, startWith, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { PaymentService } from '../../shared/payment/services/payment.service';
import { UpgradeLinkComponent } from '../upgrade-link/upgrade-link.component';
import { ThemesCrudService } from '../../shared/services/themes/themes-crud.service.ts';

enum NotificationBarState {
  MaxTeamMembersExceeded = 'MaxTeamMembersExceeded',
  MaxWorkspacesPerTeamExceeded = 'MaxWorkspacesPerTeamExceeded',
  MaxBranchesPerWorkspaceExceeded = 'MaxBranchesPerWorkspaceExceeded',
  MaxThemesPerWorkspaceExceeded = 'MaxThemesPerWorkspaceExceeded',
}

@Component({
  selector: 'dt-notification-bar',
  templateUrl: './notification-bar.component.html',
  styleUrls: ['./notification-bar.component.scss'],
  imports: [AsyncPipe, UpgradeLinkComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBarComponent {
  private selectedTeamService = inject(SelectedTeamService);
  private selectedWorkspaceService = inject(SelectedWorkspaceService);
  private themesCrudService = inject(ThemesCrudService);
  private paymentService = inject(PaymentService);

  NotificationBarState = NotificationBarState;

  customerPortalUrl = this.paymentService.customerPortalUrl;

  selectedTeam = this.selectedTeamService.selectedTeam;

  states = computed<NotificationBarState[]>(() => {
    const selectedTeam = this.selectedTeamService.selectedTeam();
    const selectedWorkspace = this.selectedWorkspaceService.selectedWorkspace();
    const themes = this.themesCrudService.allThemes();
    if (!selectedTeam || !selectedWorkspace) {
      return [];
    }
    const { plan, members, workspaces } = selectedTeam;
    const { branches } = selectedWorkspace;
    const {
      MAX_TEAM_MEMBERS,
      MAX_WORKSPACES_PER_TEAM,
      MAX_BRANCHES_PER_WORKSPACE,
      MAX_THEMES_PER_WORKSPACE,
    } = plan;
    return [
      members.length > MAX_TEAM_MEMBERS
        ? NotificationBarState.MaxTeamMembersExceeded
        : undefined,
      workspaces.length > MAX_WORKSPACES_PER_TEAM
        ? NotificationBarState.MaxWorkspacesPerTeamExceeded
        : undefined,
      branches.length > MAX_BRANCHES_PER_WORKSPACE
        ? NotificationBarState.MaxBranchesPerWorkspaceExceeded
        : undefined,
      themes.length > MAX_THEMES_PER_WORKSPACE
        ? NotificationBarState.MaxThemesPerWorkspaceExceeded
        : undefined,
    ].filter((state): state is NotificationBarState => state !== undefined);
  });

  states$ = toObservable(this.states);

  state$ = this.states$.pipe(
    switchMap((states) =>
      interval(4000).pipe(
        map((_, i) => states[i % states.length]),
        startWith(states[0]),
        delay(4000),
      ),
    ),
  );
}
